import { IAppContext } from 'App/AppContext';
import { DocumentOutputPurpose } from 'models/documentOutputLocation';
import OutputLocationBinding, {
  OutputLocationBindingEffective,
  OutputLocationBindings,
} from 'models/outputLocationBinding';
import { apiGetBindings } from 'services/Api/outputLocationBindingService';
import { apiRequest } from 'services/Auth/authConfig';
import GlobalDataCache from './globalDataCache';

/**
 * The evidence uploads bindings of the tenant: the tenant row and the template rows. Loaded with the categories,
 * because the Upload evidence button reads it to know whether a task has a category to upload to. The Reports tab
 * and the template editor call invalidate after a change.
 */
export class OutputLocationBindingCache {
  appContext?: IAppContext;

  cacheRef?: GlobalDataCache;

  private bindings: OutputLocationBindings | undefined;

  private isLoading: boolean = false;

  private bindingsPromise: Promise<OutputLocationBindings> | undefined;

  get current(): OutputLocationBindings | undefined {
    return this.bindings;
  }

  async getItems(refresh: boolean = false): Promise<OutputLocationBindings | undefined> {
    if (this.isLoading && this.bindingsPromise) {
      return await this.bindingsPromise;
    }

    if (!this.bindings || refresh) {
      if (this.appContext && !this.isLoading) {
        try {
          this.isLoading = true;
          const accessToken = await this.appContext.getAccessToken(apiRequest.scopes);
          this.bindingsPromise = apiGetBindings(DocumentOutputPurpose.EvidenceUploads, accessToken);
          this.bindings = await this.bindingsPromise;
        } catch (err) {
          this.appContext.setError(err);
        } finally {
          this.bindingsPromise = undefined;
          this.isLoading = false;
        }
      }
    }

    return this.bindings;
  }

  /** Drops the loaded bindings, so the next getItems reads them again. */
  invalidate(): void {
    this.bindings = undefined;
  }

  /** The row of the template, or undefined when the template uses the tenant row. */
  getForTemplate(templateTaskId: number | undefined): OutputLocationBinding | undefined {
    if (!templateTaskId || !this.bindings) return undefined;

    return this.bindings.templates.find((binding) => binding.templateTaskId === templateTaskId);
  }

  /**
   * True when a task has a category to upload evidence to: the first of its templates that has a row decides, else
   * the tenant row. The caller passes the task's own template and, where it knows it, the template of the series or
   * of the event the task belongs to.
   */
  hasCategoryFor(templateTaskIds: (number | undefined)[]): boolean {
    return this.getEffectiveListId(templateTaskIds) !== undefined;
  }

  /** The category a task uploads its evidence to, chosen as hasCategoryFor describes; undefined when none is bound. */
  getEffectiveListId(templateTaskIds: (number | undefined)[]): number | undefined {
    return this.getEffective(templateTaskIds)?.listId;
  }

  /** The values that apply to a task, its category and start folder included, from the row hasCategoryFor chooses. */
  getEffective(templateTaskIds: (number | undefined)[]): OutputLocationBindingEffective | undefined {
    if (!this.bindings) return undefined;

    const row = templateTaskIds.map((id) => this.getForTemplate(id)).find((binding) => binding !== undefined);

    return (row ?? this.bindings.default).effective;
  }

  clone(): OutputLocationBindingCache {
    const cache = new OutputLocationBindingCache();
    cache.bindings = this.bindings?.clone();

    return cache;
  }
}
